/**
 * **Menu administrasi (☰)** — rumah bagi fungsi yang LANGKA dan TERKENDALI.
 *
 * ### ⭐ Pembelahannya: frekuensi × risiko, bukan "penting vs tak penting"
 * Fungsi yang dipakai **tiap transaksi** (jumlah, hapus baris) tinggal di bar konteks — nol ketukan
 * tambahan. Yang masuk ke sini adalah yang **jarang dan berakibat**: buka laci, batalkan bill,
 * setelan perangkat. Ketukan tambahan di sini bukan ongkos, melainkan **gunanya**.
 *
 * ⛔ Rail VTEC menaruh keduanya berdampingan dan setara — `Del Item` bersebelahan dengan `Reprint`.
 * Akibatnya terbaca di data: alasan reprint **18% kosong**, sisanya `C`/`copy`/`duplikat`. Fungsi
 * berisiko yang semudah fungsi harian akan dipakai sesering fungsi harian.
 *
 * ### ⛔ Butir disaring IZIN — disembunyikan, bukan ditampilkan lalu ditolak
 * Tombol yang terlihat tapi selalu gagal mengajari kasir bahwa penolakan itu wajar; sesudah itu
 * penolakan yang sungguh-sungguh pun tak dibaca lagi.
 *
 * ⚠️ Yang **belum** ada jalurnya di server sengaja TIDAK dipasang di sini (cetak ulang, buka-kembali
 * bill, kirim ke dapur). Menu berisi tombol yang tak mengerjakan apa-apa adalah cara tercepat
 * membuat seluruh menu berhenti dipercaya.
 *
 * ⛔ **Sesi kas TIDAK di sini.** Ia sudah sekali ketuk lewat nama staf di pita atas — gerakan yang
 * sama dengan di dashboard meja. Dua jalan ke satu layar berarti separuh kasir menghafal jalan
 * yang salah, lalu mengira jalan itu hilang saat layarnya berganti.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { hasPermission } from '../api/session';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { ChangePinButton } from './ChangePinButton';
import { ClearCacheButton } from './ClearCacheButton';
import { UnpairButton } from './UnpairButton';
import { DrawerButton } from './DrawerButton';
import { ShiftReportButton } from './ShiftReportButton';

/**
 * Alasan pembatalan bill. ⛔ Enum kontrak (`REASON_CODES`), bukan teks bebas.
 *
 * Bukti kenapa: di VTEC alasan berupa teks bebas berakhir 18% kosong, sisanya `C`/`v`/`copy` —
 * kolom yang terisi tapi tak bisa dijawab siapa pun saat ditanya "kenapa bill ini hilang?".
 */
const VOID_REASONS = [
  'customer_cancelled',
  'duplicate_entry',
  'wrong_item',
  'kitchen_error',
  'other',
] as const;

export interface AdminMenuProps {
  readonly outletCode: string;
  readonly deviceCode: string;
  readonly businessDate?: string;
  readonly shiftId: string | null;
  /** Pesanan yang sedang terbuka di server. `null` ⇒ tak ada yang bisa dibatalkan. */
  readonly orderId: string | null;
  /**
   * ⚠️ Server terjangkau? Dipakai menahan "bersihkan salinan" saat luring — membuang satu-satunya
   * salinan menu tepat ketika ia tak bisa diambil lagi adalah kerusakan yang tak bisa dibatalkan.
   */
  readonly online?: boolean;
  readonly onVoidOrder: (reasonCode: string, reasonNote?: string) => void;
  /**
   * ⭐ Keluar dari sesi STAF — bukan menutup sesi kas.
   *
   * Satu terminal dipakai bergantian sepanjang shift. Tanpa jalan keluar, pelayan berikutnya
   * bekerja **atas nama** orang sebelumnya: tiap void, tiap diskon, tiap buka laci tercatat pada
   * orang yang salah — dan jejak yang menunjuk orang yang salah lebih buruk daripada tak ada jejak.
   */
  readonly onLogout: () => void;
  /**
   * ⭐ Membuka daftar **stok habis**. Jalur kedua menuju keadaan yang sama dengan tekan-tahan di
   * kisi menu — dan keduanya perlu: menandai terjadi sambil melihat barangnya, menyalakan kembali
   * terjadi saat orang ingin tahu **apa saja** yang sedang mati.
   */
  readonly onSoldOut: () => void;
  /**
   * ⭐⭐⭐ Membuka **cari bill**. Sampai layar ini ada, refund cuma bisa dijangkau dari bill yang
   * barusan lunas di terminal itu — tamu yang komplain besok tak punya jalan sama sekali.
   */
  readonly onFindBill: () => void;
  /**
   * ⭐⭐ Membuka **penjualan hari ini**. ⛔ Bergerbang izin `report:view` di server DAN disaring di
   * sini: X-report menjawab "berapa yang harus di laci", yang ini menjawab "barang apa yang
   * bergerak" — dua pertanyaan yang sering dibutuhkan orang yang sama, tapi tak boleh menumpang
   * satu lembar (lembar setoran yang penuh baris menu berhenti dibaca).
   */
  readonly onSalesToday: () => void;
  /**
   * ⭐⭐⭐ Membuka **pilih printer** — dari yang AGEN lihat, bukan dari yang orang ketik.
   *
   * ⛔ Tanpa saringan izin: memasang printer adalah pekerjaan orang yang berdiri di depan tablet
   * pada hari pertama, bukan wewenang manajer. Yang menuntut izin master adalah menyetel printer
   * JARINGAN di portal — di sana alamatnya memang keputusan IT.
   */
  readonly onPrinterSetup: () => void;
}

export function AdminMenu(p: AdminMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState<(typeof VOID_REASONS)[number]>('customer_cancelled');
  const [note, setNote] = useState('');
  const box = useRef<HTMLDivElement>(null);

  /*
   * ⚠️ Ketukan di LUAR menutup panel. Tanpa ini, panel yang terbuka menutupi bill dan kasir harus
   * menemukan tombol ☰ lagi untuk melihat angkanya sendiri — persis saat ia sedang memutuskan.
   */
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current !== null && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => { document.removeEventListener('mousedown', away); };
  }, [open]);

  // ⛔ `other` tanpa penjelasan = teks bebas jilid dua. Layar menolak lebih dulu; server tetap jaring.
  const voidBlocked = reason === 'other' && note.trim() === '';

  return (
    <div className="admin" ref={box}>
      <button
        type="button"
        className="admin-toggle"
        data-testid="admin-menu"
        aria-label={t('cashier.admin.title')}
        aria-expanded={open}
        onClick={() => { setOpen((v) => !v); }}
      >
        ☰
      </button>

      {open && (
        <div className="admin-panel" role="menu" data-testid="admin-panel">
          <h2>{t('cashier.admin.title')}</h2>

          {hasPermission('drawer:open') && (
            <div className="admin-item">
              <DrawerButton shiftId={p.shiftId} deviceCode={p.deviceCode} />
            </div>
          )}

          {hasPermission('order:void') && (
            <div className="admin-item">
              <button
                type="button"
                className="action-secondary"
                data-testid="void-order"
                disabled={p.orderId === null}
                // ⚠️ Menyebut SEBABNYA mati: bill belum dibuat, jadi tak ada yang bisa dibatalkan.
                title={p.orderId === null ? t('cashier.admin.voidNeedsBill') : undefined}
                onClick={() => { setVoidOpen(true); }}
              >
                {t('cashier.admin.void')}
              </button>
            </div>
          )}

          {hasPermission('report:view') && (
            <div className="admin-item">
              <button
                type="button"
                className="action-secondary"
                data-testid="admin-sales-today"
                onClick={() => { setOpen(false); p.onSalesToday(); }}
              >
                {t('cashier.sales.button')}
              </button>
            </div>
          )}

          {/*
            ⭐⭐⭐ **X-report DI SINI, bukan di dalam layar "Tutup Sesi Kas"** (2026-08-25).

            ⛔ Sebelumnya satu-satunya jalan ke X adalah membuka layar penutupan — layar yang
            seluruh isinya tentang MENGAKHIRI sesi. Meminta orang berdiri di depan tombol itu
            hanya untuk membaca posisi laci tengah hari adalah meminta kecelakaan.

            ⚠️ X ≠ Z, dan bedanya harus terlihat di labelnya: X **tak menutup apa pun** dan boleh
            ditekan sesering apa pun; Z mengakhiri sesi. Dua cetakan yang isinya mirip tapi
            akibatnya sangat berbeda tak boleh terbaca sama.
          */}
          {hasPermission('report:view') && p.shiftId !== null && (
            <div className="admin-item">
              <ShiftReportButton shiftId={p.shiftId} kind="x" />
            </div>
          )}

          {/*
            ⭐⭐⭐ **Cari bill** — pintu untuk cetak ulang struk lama DAN refund bill yang bukan
            transaksi terakhir. ⛔ Tanpa saringan izin: server yang menjaganya (`order:create`),
            dan mencetak ulang struk untuk tamu yang kembali adalah pekerjaan kasir sehari-hari,
            bukan wewenang manajer. ⚠️ Yang bergerbang `report:view` tetap AGREGAT-nya (X/Z).
          */}
          <div className="admin-item">
            <button
              type="button"
              className="action-secondary"
              data-testid="admin-find-bill"
              onClick={() => { setOpen(false); p.onFindBill(); }}
            >
              {t('cashier.find.button')}
            </button>
          </div>

          {/*
            ⭐ Daftar stok habis. ⛔ Tanpa saringan izin — yang tahu barangnya habis adalah orang
            yang berdiri di depan tamu (2026-08-23); jejaknya yang dibuat tegas, bukan gerbangnya.
          */}
          <div className="admin-item">
            <button
              type="button"
              className="action-secondary"
              data-testid="admin-soldout"
              onClick={() => { setOpen(false); p.onSoldOut(); }}
            >
              {t('cashier.soldOut.title')}
            </button>
          </div>

          {/*
            ⭐ Ubah PIN duduk TEPAT DI ATAS "Keluar" — dua-duanya soal identitas orang yang sedang
            memegang terminal, dan yang mencari salah satunya akan melihat keduanya.
            ⛔ Tanpa saringan izin: mengganti PIN sendiri hak SETIAP staf.
          */}
          <div className="admin-item">
            <ChangePinButton />
          </div>

          {/*
            ⭐⭐ **Muat ulang** (Imron, 2026-08-22 — sesudah versi baru tak sampai ke tabletnya).
            ⛔ PWA terpasang tak punya bilah alamat, dan `overscroll-behavior: none` mematikan
            tarik-untuk-menyegarkan (disengaja: ia memuat ulang di tengah bill). Hasilnya: terminal
            yang TAK PUNYA cara apa pun mengambil versi baru — dan yang menemukannya adalah orang
            yang sudah di-deploy perbaikannya tapi tak melihat perubahan apa pun.
            ⚠️ Aman ditekan kapan saja: baris yang belum didorong memang hilang, tapi antrean
            `outbox` (penjualan yang sudah dibayar) selamat — ia di `localStorage`.
          */}
          <div className="admin-item">
            <button
              type="button"
              className="action-secondary"
              data-testid="reload-app"
              onClick={() => { location.reload(); }}
            >
              {t('cashier.admin.reload')}
            </button>
          </div>

          {/*
            ⛔ **Unduh APK agen DIBUANG dari sini** (Imron, 2026-08-23).

            Ia ditaruh di sini 2026-08-22 karena lencana printer hanya menawarkannya saat agen TAK
            ADA — jadi sesudah terpasang, tak ada lagi jalan mengambil versi baru. Alasannya benar,
            tempatnya salah: memasang agen adalah pekerjaan **sekali seumur tablet**, sementara ☰
            dibuka berkali-kali tiap shift. Menu ini dibelah menurut frekuensi × risiko (lihat
            kepala berkas), dan butir yang dipakai sekali lalu tak pernah lagi cuma menambah
            panjang daftar yang harus dibaca kasir saat ia sedang mencari yang lain.

            ⇒ Jalurnya tetap ada dan tetap satu: **lencana printer** saat agen tak menjawab. Yang
            butuh memasang ulang justru berada persis di keadaan itu.
          */}
          <div className="admin-item">
            <button
              type="button"
              className="action-secondary"
              data-testid="logout"
              onClick={() => { setOpen(false); p.onLogout(); }}
            >
              {t('cashier.admin.logout')}
            </button>
          </div>

          {/*
            ⭐ Bersihkan salinan duduk di GRUP SETELAN, bukan di antara aksi harian: ia perawatan
            perangkat, bukan perbuatan atas bill. ⛔ Disaring `master:edit` — disembunyikan, bukan
            ditampilkan lalu ditolak.
          */}
          <div className="admin-group" data-testid="admin-settings">
            <h3>{t('cashier.admin.settings')}</h3>
            {/*
              ⭐⭐ **Lepas perangkat** — satu-satunya jalan mengubah outlet/peran perangkat dari
              dalam aplikasi. ⛔ Sebelum ini tak ada: `ClearCacheButton` sengaja MENYIMPAN
              pasangannya (benar untuk kasusnya), jadi perangkat terkunci selamanya ke outlet
              pertamanya dan satu-satunya jalan keluar adalah DevTools — persis yang layar
              pemasangan ada untuk menghapusnya.
              ⚠️ Duduk tepat di samping "bersihkan salinan": keduanya berpintu PIN administrator.
            */}
            {/*
              ⭐⭐ **Pilih printer** — duduk di kelompok setelan perangkat, bukan di aksi harian:
              ia dipakai sekali saat memasang, lalu hampir tak pernah lagi.
            */}
            <div className="admin-item">
              <button
                type="button"
                className="action-secondary"
                data-testid="admin-printer-setup"
                onClick={() => { setOpen(false); p.onPrinterSetup(); }}
              >
                {t('cashier.printerSetup.button')}
              </button>
            </div>

            {hasPermission('master:edit') && <UnpairButton />}
            {hasPermission('master:edit') && (
              <ClearCacheButton
                outletCode={p.outletCode}
                deviceCode={p.deviceCode}
                online={p.online !== false}
              />
            )}
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>

          {/*
            ⭐ Identitas perangkat DITAMPILKAN, tak disembunyikan di menu teknisi.
            Saat outlet menelepon, pertanyaan pertama selalu "terminal yang mana?" — dan jawabannya
            harus bisa dibaca kasir tanpa dipandu klik demi klik lewat telepon (P10).
          */}
          <dl className="admin-info" data-testid="admin-device">
            <dt>{t('cashier.admin.outlet')}</dt>
            <dd>{p.outletCode}</dd>
            <dt>{t('cashier.admin.device')}</dt>
            <dd>{p.deviceCode}</dd>
            {p.businessDate !== undefined && (
              <>
                <dt>{t('cashier.businessDate')}</dt>
                <dd>{p.businessDate}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {voidOpen && (
        <div
          className="dialog"
          role="dialog"
          aria-label={t('cashier.admin.void')}
          data-testid="void-dialog"
        >
          <h2>{t('cashier.admin.void')}</h2>
          {/*
            ⚠️ Peringatan lebih dulu, PIN-nya sudah dijawab sesi staf + izin `order:void`. Yang
            ditegakkan di sini adalah ALASAN — pembatalan tanpa alasan tak bisa ditinjau siapa pun.
          */}
          <p className="dialog-note">{t('cashier.admin.voidWarning')}</p>

          <label>
            {t('cashier.drawer.reason')}
            <select
              data-testid="void-reason"
              value={reason}
              onChange={(e) => { setReason(e.target.value as (typeof VOID_REASONS)[number]); }}
            >
              {VOID_REASONS.map((r) => (
                <option key={r} value={r}>{t(`enum.reasonCode.${r}`)}</option>
              ))}
            </select>
          </label>

          {reason === 'other' && (
            <label>
              {t('cashier.drawer.note')}
              <input
                data-testid="void-note"
                value={note}
                onChange={(e) => { setNote(e.target.value); }}
              />
            </label>
          )}

          <div className="dialog-actions">
            <button
              type="button"
              data-testid="void-cancel"
              onClick={() => { setVoidOpen(false); setNote(''); }}
            >
              {t('cashier.drawer.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="void-confirm"
              disabled={voidBlocked}
              onClick={() => {
                p.onVoidOrder(reason, note.trim() === '' ? undefined : note.trim());
                setVoidOpen(false);
                setOpen(false);
                setNote('');
              }}
            >
              {t('cashier.admin.voidConfirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
